import { logger } from "@/modules/logger";
import { uuidModule } from "@/modules/uuid";
import { BaseSyncOperation } from "@/store/sync/operations/BaseSyncOperation";
import { IUpdateAudioRecordingOperation } from "@/store/sync/operations/types";
import { OptimisticSyncUpdate, SyncModelData, SyncOperationKind } from "@/store/sync/types";
import {
  AudioRecordingModelData,
  AudioRecordingProcessingStatus,
  AudioRecordingRecordingStatus,
  AudioRecordingUploadStatus,
} from "@/store/audio-recordings/types";

export class UpdateAudioRecordingOperation extends BaseSyncOperation<IUpdateAudioRecordingOperation> {
  get operationKind(): SyncOperationKind {
    return "UPDATE_AUDIO_RECORDING";
  }

  public async generateOptimisticUpdates(): Promise<OptimisticSyncUpdate<SyncModelData>[]> {
    const recording = this.store.audioRecordings.get(this.payload.id);
    if (!recording) {
      logger.debug({
        message: "[UpdateAudioRecordingOperation] attempted to update missing audio recording",
        info: { recordingId: this.payload.id },
      });
      return [];
    }

    const recordingStatus: AudioRecordingRecordingStatus = (this.payload.recording_status ||
      recording.modelData.recording_status) as AudioRecordingRecordingStatus;

    const uploadStatus: AudioRecordingUploadStatus = (this.payload.upload_status ||
      recording.modelData.upload_status) as AudioRecordingUploadStatus;

    const processingStatus: AudioRecordingProcessingStatus = (this.payload.processing_status ||
      recording.modelData.processing_status) as AudioRecordingProcessingStatus;

    const locallyModifiedAt = this.committedAt ?? recording.locallyModifiedAt;

    const associatedClientId =
      this.payload.associated_client_id === undefined
        ? (recording.modelData.associated_client_id ?? null)
        : (this.payload.associated_client_id ?? null);

    const associatedNoteId =
      this.payload.associated_note_id === undefined
        ? (recording.modelData.associated_note_id ?? null)
        : (this.payload.associated_note_id ?? null);

    const optimisticUpdate: OptimisticSyncUpdate<AudioRecordingModelData> = {
      optimistic_update_id: uuidModule.generate(),
      locally_committed_at: this.committedAt,
      kind: "UPSERTED",
      value: {
        model_id: this.payload.id,
        model_kind: "AUDIO_RECORDING",
        model_version: recording.modelVersion,
        model_data: {
          ...recording.modelData,
          recording_status: recordingStatus,
          upload_status: uploadStatus,
          processing_status: processingStatus,
          locally_modified_at: locallyModifiedAt,
          associated_note_id: associatedNoteId,
          associated_client_id: associatedClientId,
        },
        model_scopes: recording.data.model_scopes,
      },
    };

    return [optimisticUpdate];
  }

  async triggerRecompute() {
    await this.store.audioRecordings.recompute(this.payload.id);
  }
}
